import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import Seo from '../components/Seo.jsx'
import { ProjectStructuredData, BreadcrumbStructuredData } from '../components/StructuredData.jsx'
import Container from '../components/Container.jsx'
import Reveal from '../components/Reveal.jsx'
import CTA from '../components/CTA.jsx'

import feature1 from '../assets/features/feature-1.jpg'
import feature2 from '../assets/features/feature-2.jpg'
import feature3 from '../assets/features/feature-3.jpg'
import feature4 from '../assets/features/feature-4.jpg'
import feature5 from '../assets/features/feature-5.jpg'
import feature6 from '../assets/features/feature-6.jpg'
import feature7 from '../assets/features/feature-7.jpg'
import feature8 from '../assets/features/feature-8.jpg'

/* =========================================================================
   ProjectShowcaseDetail — one client project from the showcase.
   Reads the same endpoint the index uses and picks the row by id, rather
   than adding a second endpoint for six records.
   ========================================================================= */

const API_BASE = import.meta.env.VITE_API_BASE || '/api'
import { showcaseImage } from '../lib/showcaseImage.js'
import CountryTag from '../components/CountryTag.jsx'
const ACCENTS = ['#67e8f9', '#a78bfa', '#008eff', '#fb9a8f', '#fbbf24', '#34d399']

const resolveImage = showcaseImage

/* The capabilities that ship with every build. Generic on purpose - the
   showcase_project table carries no per-project feature rows, so this is a
   shared set rather than invented client-specific claims. Swap for real
   per-project content once that data exists. */
const PROJECT_FEATURES = [
  {
    title: 'Responsive by default',
    caption: 'One build that holds together from a 360px phone to a widescreen desktop.',
    image: feature6, icon: 'devices',
  },
  {
    title: 'Content you control',
    caption: 'Copy, images and sections editable in-house, with no developer needed for a text change.',
    image: feature7, icon: 'edit',
  },
  {
    title: 'Search-ready',
    caption: 'Clean markup, metadata, sitemaps and structured data wired in from day one.',
    image: feature8, icon: 'search',
  },
  {
    title: 'Fast page loads',
    caption: 'Compressed assets, lazy media and sensible caching so pages open on weak connections.',
    image: feature2, icon: 'bolt',
  },
  {
    title: 'Secure by default',
    caption: 'HTTPS, hardened forms, spam protection and dependency updates as standard.',
    image: feature3, icon: 'shield',
  },
  {
    title: 'Analytics built in',
    caption: 'Traffic, sources and conversions tracked, so decisions come from numbers.',
    image: feature4, icon: 'chart',
  },
  {
    title: 'Managed hosting',
    caption: 'Deployment, domains, SSL and backups handled, so the site stays up unattended.',
    image: feature5, icon: 'server',
  },
  {
    title: 'Support after launch',
    caption: 'Fixes, tweaks and feature additions from the same team that built it.',
    image: feature1, icon: 'person',
  },
]

/* The badge under each card's artwork. Line icons on a 24px grid so they sit
   at the same weight as the rest of the page. */
const FEATURE_ICONS = {
  devices: (<><rect x="2.5" y="4" width="13" height="9.5" rx="1.4" /><path d="M6 17h6" /><rect x="17" y="8" width="4.5" height="12" rx="1.2" /></>),
  edit: (<><path d="M4 4.5h9.5M4 9h9.5M4 13.5h5" /><path d="M20.4 10.6a1.9 1.9 0 0 0-2.7-2.7l-6 6-.6 3.3 3.3-.6 6-6Z" /></>),
  search: (<><circle cx="10.5" cy="10.5" r="6.5" /><path d="M15.4 15.4 21 21" /></>),
  bolt: (<path d="M13 2 4.5 13.5H11l-1 8.5 8.5-11.5H12l1-8.5Z" />),
  shield: (<><path d="M12 3l7.5 3v5.5c0 4.4-3.1 8.3-7.5 9.5-4.4-1.2-7.5-5.1-7.5-9.5V6L12 3Z" /><path d="M9.2 12.2l2 2 3.6-4" /></>),
  chart: (<><path d="M4 20V4M4 20h16" /><path d="M8 20v-6M12.5 20V8M17 20v-9" /></>),
  server: (<><rect x="3" y="4" width="18" height="6" rx="1.6" /><rect x="3" y="14" width="18" height="6" rx="1.6" /><path d="M7 7h.01M7 17h.01" /></>),
  person: (<><circle cx="12" cy="8" r="3.6" /><path d="M4.8 20a7.2 7.2 0 0 1 14.4 0" /></>),
}

/* Reasons to build with Spectranox. Shared across projects for the same
   reason PROJECT_FEATURES is: the showcase_project table holds no per-project
   rows for this. The count in the heading is derived, never hard-coded. */
const BUILD_REASONS = [
  { title: 'One accountable team', body: 'Design, build and launch handled by the same people. No hand-offs, no finger-pointing.' },
  { title: 'Shipped, not demoed', body: 'Every project here is live and in use. We measure done by traffic, not by mockups.' },
  { title: 'You own everything', body: 'Repository, hosting, domain and content. Full transfer on day one, no lock-in.' },
  { title: 'Built to be edited', body: 'Your team changes copy and images without a developer or a support ticket.' },
  { title: 'Honest timelines', body: 'A date we can hold, given up front, with weekly progress you can actually see.' },
  { title: 'Fixed, clear pricing', body: 'Scope and cost agreed before work starts. No surprise line items at handover.' },
  { title: 'Real support after launch', body: 'The engineers who built it answer when something needs changing.' },
  { title: 'Standards, not shortcuts', body: 'Accessible markup, sensible performance budgets and dependencies kept current.' },
]

export default function ProjectShowcaseDetail() {
  const { id } = useParams()
  const [projects, setProjects] = useState([])
  const [status, setStatus] = useState('loading') // loading | ready | error

  useEffect(() => {
    let cancelled = false

    fetch(`${API_BASE}/get-showcase-projects.php`, { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return
        if (data && data.success && Array.isArray(data.projects)) {
          setProjects(data.projects)
          setStatus('ready')
        } else {
          setStatus('error')
        }
      })
      .catch(() => { if (!cancelled) setStatus('error') })

    return () => { cancelled = true }
  }, [])

  const index = useMemo(
    () => projects.findIndex((p) => String(p.id) === String(id)),
    [projects, id]
  )
  const project = index >= 0 ? projects[index] : null
  const accent = ACCENTS[(index < 0 ? 0 : index) % ACCENTS.length]

  // preview_images is stored as a JSON array of image paths (same shape as
  // project_image). Tolerate a null column, a raw array, or a JSON string.
  const previewImages = useMemo(() => {
    const raw = project?.preview_images
    if (!raw) return []
    if (Array.isArray(raw)) return raw.filter(Boolean)
    try {
      const arr = JSON.parse(raw)
      return Array.isArray(arr) ? arr.filter(Boolean) : []
    } catch {
      return []
    }
  }, [project])

  const stack = String(project?.tech_stack || '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)

  // Keep browsing without going back to the index first.
  const next = projects.length > 1 && index >= 0 ? projects[(index + 1) % projects.length] : null

  if (status === 'loading') {
    return (
      <main className="pt-20">
        <Container>
          <p className="py-32 text-center text-sm text-slate-500">Loading project…</p>
        </Container>
      </main>
    )
  }

  if (status === 'error' || !project) {
    return (
      <>
        <Seo title="Project not found" path={`/portfolio/${id}`} noIndex />
        <main className="pt-20">
          <Container>
            <div className="py-28 text-center">
              <h1 className="font-display text-3xl font-bold text-white">We couldn’t find that project</h1>
              <p className="mt-3 text-sm text-slate-400">It may have been renamed or taken down.</p>
              <Link
                to="/portfolio"
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-5 py-2.5 text-sm font-semibold text-white transition hover:border-azure-400/60"
              >
                Back to all projects
              </Link>
            </div>
          </Container>
        </main>
      </>
    )
  }

  return (
    <>
      <Seo
        title={project.title}
        description={project.description || `${project.title}, a client project designed and built by Spectranox.`}
        path={`/portfolio/${project.id}`}
        image={resolveImage(project.project_image)}
      />
      <ProjectStructuredData
        name={project.title}
        description={project.description || `${project.title}, a client project designed and built by Spectranox.`}
        image={resolveImage(project.project_image)}
        path={`/portfolio/${project.id}`}
        year={project.year}
      />
      <BreadcrumbStructuredData
        trail={[
          { name: 'Home', path: '/' },
          { name: 'Portfolio', path: '/portfolio' },
          { name: project.title, path: `/portfolio/${project.id}` },
        ]}
      />
      <main className="pt-20">
        <section className="relative overflow-hidden py-12 lg:py-16">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{ background: `radial-gradient(60% 45% at 60% 0%, ${accent}1a, transparent 70%)` }}
          />
          <div className="relative mx-auto w-full max-w-[1330px] px-5">
            <div className="grid gap-12 lg:grid-cols-12 lg:gap-14">
              {/* Screenshot */}
              <Reveal className="order-2 lg:order-1 lg:col-span-7">
                <figure className="overflow-hidden rounded-2xl border border-white/10 bg-ink-950 shadow-2xl">
                  <img
                    src={resolveImage(project.project_image)}
                    alt={`${project.title} website`}
                    className="block h-auto w-full object-cover object-top"
                    loading="eager"
                    decoding="async"
                  />
                </figure>
              </Reveal>

              {/* Write-up — the title block leads this column so the page opens
                  on the copy rather than a separate banner. */}
              <div className="order-1 lg:order-2 lg:col-span-5">
                <Reveal>
                  <Link
                    to="/portfolio"
                    className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400 transition-colors hover:text-white"
                  >
                    <span aria-hidden>←</span> All projects
                  </Link>
                </Reveal>

                <Reveal delay={60}>
                  <CountryTag code={project.country_code} className="mt-4" />
                  <h1 className="mt-3 font-display text-[clamp(1.9rem,3.4vw,2.75rem)] font-bold leading-[1.08] tracking-tight text-white">
                    {project.title}
                  </h1>
                </Reveal>

                <Reveal delay={100}>
                  <p className="mt-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
                    {project.categories} · {project.year}
                  </p>
                </Reveal>

                <span aria-hidden className="mt-7 mb-1 block h-px w-full bg-white/10" />

                {project.description && (
                  <Reveal delay={80}>
                    <h2 className="mt-7 font-display text-xl font-bold tracking-tight text-white">About this project</h2>
                    <p className="mt-4 text-base leading-relaxed text-slate-300">{project.description}</p>
                  </Reveal>
                )}


                {next && (
                  <Reveal delay={250}>
                    <Link
                      to={`/portfolio/${next.id}`}
                      className="group/next mt-10 flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4 transition-all hover:-translate-y-0.5 hover:border-azure-400/50"
                    >
                      <span className="min-w-0">
                        <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">Next project</span>
                        <span className="mt-0.5 block truncate font-display text-base font-semibold text-white">{next.title}</span>
                      </span>
                      <span aria-hidden className="text-azure-400 transition-transform group-hover/next:translate-x-0.5">→</span>
                    </Link>
                  </Reveal>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Problem / Solution — genuinely per-project. Each column shows only
            when the admin has entered that side; the whole section hides when
            neither is set. Both present → two columns; one present → a single
            centred column. */}
        {(project.problem || project.solution) && (
          <section className="relative overflow-hidden border-t border-white/10 py-16 lg:py-24">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
              style={{ background: `radial-gradient(50% 50% at 50% 0%, ${accent}12, transparent 70%)` }}
            />
            <div className="relative mx-auto w-full max-w-[1330px] px-5">
              <Reveal>
                <div className="mx-auto max-w-2xl text-center">
                  <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-bold tracking-tight text-white">
                    The problem, and how we solved it
                  </h2>
                  <span aria-hidden className="mx-auto mt-4 block h-px w-16 bg-azure-400/60" />
                </div>
              </Reveal>

              <div
                className={`mt-12 grid gap-6 lg:gap-8 ${project.problem && project.solution ? 'lg:grid-cols-2' : 'mx-auto max-w-3xl'
                  }`}
              >
                {project.problem && (
                  <Reveal className="h-full">
                    <article className="flex h-full flex-col rounded-2xl border border-rose-400/20 bg-rose-500/[0.05] p-7 lg:p-9">
                      <span className="inline-flex items-center gap-2 self-start rounded-full border border-rose-400/30 bg-rose-400/10 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-rose-300">
                        <span aria-hidden className="size-1.5 rounded-full bg-rose-400" /> The problem
                      </span>
                      <h3 className="mt-5 font-display text-xl font-bold tracking-tight text-white lg:text-2xl">
                        What the client faced
                      </h3>
                      <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-slate-300">
                        {project.problem}
                      </p>
                    </article>
                  </Reveal>
                )}

                {project.solution && (
                  <Reveal delay={80} className="h-full">
                    <article className="flex h-full flex-col rounded-2xl border border-emerald-400/25 bg-emerald-500/[0.06] p-7 lg:p-9">
                      <span className="inline-flex items-center gap-2 self-start rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-300">
                        <span aria-hidden className="size-1.5 rounded-full bg-emerald-400" /> Our solution
                      </span>
                      <h3 className="mt-5 font-display text-xl font-bold tracking-tight text-white lg:text-2xl">
                        What we built
                      </h3>
                      <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-slate-300">
                        {project.solution}
                      </p>
                    </article>
                  </Reveal>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Project preview — screenshots of the delivered work. Hidden entirely
            when the admin has not added any preview images. Grid flex-wraps and
            centres, so 1 image or 8 both look intentional. */}
        {previewImages.length > 0 && (
          <section className="border-t border-white/10 py-16 lg:py-24">
            <div className="mx-auto w-full max-w-[1330px] px-5">
              <Reveal>
                <div className="mx-auto max-w-2xl text-center">
                  <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-bold tracking-tight text-white">
                    Project preview
                  </h2>
                  <span aria-hidden className="mx-auto mt-4 block h-px w-16 bg-azure-400/60" />
                  <p className="mt-4 text-base leading-relaxed text-slate-400">
                    A closer look at the screens we shipped.
                  </p>
                </div>
              </Reveal>

              <div className="mt-12 flex flex-wrap justify-center gap-5">
                {previewImages.slice(0, 8).map((src, i) => (
                  <Reveal key={`${src}-${i}`} delay={(i % 4) * 60}>
                    <figure className="w-full max-w-[340px] overflow-hidden rounded-2xl border border-white/10 bg-ink-950 shadow-xl transition-transform duration-300 hover:-translate-y-1 sm:w-[300px] lg:w-[330px]">
                      <img
                        src={resolveImage(src)}
                        alt={`${project.title} screenshot ${i + 1}`}
                        loading="lazy"
                        className="block h-auto w-full object-cover object-top"
                      />
                    </figure>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ================= FEATURES =================
            Eyebrow chip, two-tone headline, then a 4x2 grid of artwork
            cards. Each card is artwork, an icon badge, title and caption,
            in that order, so the eye lands on the picture first. */}
        <section className="relative overflow-hidden border-t border-white/10 py-16 lg:py-24">
          {/* Faint grid, so the dark ground is not flat behind the cards. */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-[0.35]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(148,163,184,0.05) 1px, transparent 1px),' +
                ' linear-gradient(90deg, rgba(148,163,184,0.05) 1px, transparent 1px)',
              backgroundSize: '64px 64px',
              maskImage: 'radial-gradient(80% 60% at 50% 0%, #000 30%, transparent 100%)',
              WebkitMaskImage: 'radial-gradient(80% 60% at 50% 0%, #000 30%, transparent 100%)',
            }}
          />

          <div className="relative mx-auto w-full max-w-[1330px] px-5">
            <Reveal delay={80}>
              <div className="mx-auto max-w-3xl text-center">
                <span className="inline-flex items-center rounded-full border border-azure-400/25 bg-azure-400/[0.08] px-4 py-1.5 font-sans text-[11px] font-bold uppercase tracking-[0.18em] text-azure-300">
                  Built for impact
                </span>

                <h2 className="mt-6 font-display text-[clamp(2rem,5vw,3.75rem)] font-extrabold leading-[1.05] tracking-tight text-white">
                  Features in{' '}
                  <span
                    className="bg-clip-text text-transparent"
                    style={{
                      backgroundImage: 'linear-gradient(90deg, #2563eb 0%, #38bdf8 55%, #67e8f9 100%)',
                      WebkitBackgroundClip: 'text',
                      backgroundClip: 'text',
                    }}
                  >
                    every build
                  </span>
                </h2>

                <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
                  The systems, performance and support that make every launch stronger.
                </p>
              </div>
            </Reveal>

            <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:mt-14 lg:gap-6 xl:grid-cols-4">
              {PROJECT_FEATURES.map((f, i) => (
                <Reveal key={f.title} delay={(i % 4) * 70} className="h-full">
                  <article className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/[0.09] bg-white/[0.025] p-2.5 transition-all duration-300 hover:-translate-y-1 hover:border-azure-400/30 hover:bg-white/[0.045]">
                    {/* Artwork is inset and separately rounded, as in the
                        reference; 16:9 matches the source files exactly so
                        nothing is cropped. */}
                    <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-ink-950">
                      <img
                        src={f.image}
                        alt=""
                        width={900}
                        height={506}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                    </div>

                    <div className="flex flex-1 flex-col px-3 pb-3 pt-4">
                      <span
                        aria-hidden="true"
                        className="grid size-10 shrink-0 place-items-center rounded-xl border border-azure-400/20 bg-azure-400/10 text-azure-300 transition-colors duration-300 group-hover:bg-azure-400/[0.18]"
                      >
                        <svg viewBox="0 0 24 24" className="size-[18px]" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                          {FEATURE_ICONS[f.icon]}
                        </svg>
                      </span>

                      <h3 className="mt-4 font-display text-[17px] font-bold tracking-tight text-white">
                        {f.title}
                      </h3>
                      <p className="mt-2 text-[14px] leading-relaxed text-slate-400">
                        {f.caption}
                      </p>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Split showcase — the one section here that is genuinely per-project */}
        <section className="relative overflow-hidden border-t border-white/10 bg-ink-900 py-16 lg:py-24">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{ background: `radial-gradient(45% 55% at 15% 40%, ${accent}14, transparent 70%)` }}
          />
          <div className="relative mx-auto w-full max-w-[1330px] px-5">
            <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-16">
              <div className="lg:col-span-5">
                <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-bold leading-[1.1] tracking-tight text-white">
                  Built for {project.categories.split(',')[0].trim().toLowerCase()}, and for the people running it
                </h2>
                <p className="mt-5 text-base leading-relaxed text-slate-300">
                  {project.description}
                </p>
                <Link
                  to="/contact"
                  className="btn-on-solid mt-8 inline-flex items-center gap-2 rounded-full bg-azure-400 px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-azure-500"
                >
                  Start a project like this
                  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </Link>
              </div>

              <div className="lg:col-span-7">
                <figure className="overflow-hidden rounded-2xl border border-white/10 bg-ink-950 shadow-2xl">
                  <img
                    src={resolveImage(project.project_image)}
                    alt={`${project.title} interface`}
                    loading="lazy"
                    className="block h-auto w-full object-cover object-top"
                  />
                </figure>
              </div>
            </div>
          </div>
        </section>

        {/* Reasons — big-number lead-in + text grid */}
        <section className="relative overflow-hidden border-t border-white/10 bg-ink-900 py-16 lg:py-24">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 top-4 select-none text-center font-display text-[clamp(5rem,16vw,13rem)] font-extrabold leading-none tracking-tight text-white/[0.03]"
          >
            REASONS
          </span>
          <div className="relative mx-auto w-full max-w-[1330px] px-5">
            <Reveal>
              <div className="mx-auto max-w-2xl text-center">
                <p className="font-display text-[clamp(2.5rem,6vw,4rem)] font-extrabold leading-none text-azure-400">
                  {BUILD_REASONS.length}
                </p>
                <h2 className="mt-3 font-display text-[clamp(1.75rem,3vw,2.5rem)] font-bold tracking-tight text-white">
                  Reasons clients build with Spectranox
                </h2>
                <p className="mt-3 text-base leading-relaxed text-slate-400">
                  The same working agreement behind every project on this page.
                </p>
              </div>
            </Reveal>

            <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {BUILD_REASONS.map((r, i) => (
                <Reveal key={r.title} delay={(i % 4) * 60} className="h-full">
                  <article className="flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.03] p-6 transition-colors hover:border-white/20">
                    <h3 className="font-display text-base font-bold tracking-tight text-white">{r.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{r.body}</p>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <CTA
          title="Want something like this for your business?"
          subtitle="Tell us what you're building. We'll scope it, design it and ship it."
          secondaryLabel="See more projects"
          secondaryTo="/portfolio"
        />
      </main>
    </>
  )
}
